import React, { useEffect, useState} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { EventService } from "../services/EventService/EventService";
import { Category } from "../types/Category";
import EventDetails from "../types/EventDetails";
import PageHelper from "./PageHelper";

const Event: React.FC = () => {
    const { id: eventId } = useParams<{ id?: string }>();               // Get the event ID parameter from the URL
    const { catId: categoryIdParam } = useParams<{ catId?: string }>(); // Get the category ID parameter from the URL
    const [eventDetails, setEventDetails] = useState<EventDetails>();
    const [categoryId, setCategoryId] = useState<string>();             // State for category ID
    const [selectedCategory, setSelectedCategory] = useState<Category>();
    const [searchParams, setSearchParams] = useSearchParams();          // Manage search parameters
    const [error, setError] = useState<string | null>(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState<number>(1);
    const picturesPerPage = 9; // Number of pictures per page

    useEffect(() => {
        const loadEventDetails = async () => {
            try {
                // Get the event ID from the URL
                const selectedEventId = eventId || "";
                if (!selectedEventId) {
                    throw new Error("Event ID not provided");
                }
                // Fetch the event details
                const eventDetails = await EventService.fetchEventDetails(selectedEventId);
                setEventDetails(eventDetails);
                
                // Initialize categoryId if not provided
                if (!categoryIdParam && eventDetails.categories.length > 0) {
                    setCategoryId(eventDetails.categories[0].id); // Default to the first category's ID
                } else {
                    setCategoryId(categoryIdParam);
                }
                
            } catch (err) {
                setError(err instanceof Error ? err.message : "Unknown error");
            }
        };
        loadEventDetails();
    }, [eventId, categoryIdParam]);

    // Separate useEffect to update selectedCategory when categoryId or eventDetails changes
    useEffect(() => {
        if (eventDetails && categoryId) {
            const category = eventDetails.categories.find((c) => c.id === categoryId);
            setSelectedCategory(category);
        }
    }, [categoryId, eventDetails]);

    // Sync currentPage with the "pg" search parameter
    useEffect(() => {
        const pgParam = searchParams.get("pg");
        const pageNumber = pgParam ? parseInt(pgParam, 10) : 1;
        if (!isNaN(pageNumber) && pageNumber > 0) {
            setCurrentPage(pageNumber);
        }
    }, [searchParams]);

    // Update the "pg" search parameter when currentPage changes
    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearchParams({ pg: currentPage.toString() });
        }, 300); // Debounce for 300ms

        return () => clearTimeout(timeout);
    }, [currentPage, setSearchParams]);

    if (error) {
        return <div>Error: {error}</div>;
    }

    // Calculate the pictures to display for the current page
    const indexOfLastPicture = currentPage * picturesPerPage;
    const indexOfFirstPicture = indexOfLastPicture - picturesPerPage;
    const currentPictures = selectedCategory?.pictures.slice(indexOfFirstPicture, indexOfLastPicture);

    // Calculate total pages
    const totalPages = selectedCategory ? Math.ceil(selectedCategory.pictures.length / picturesPerPage) : 0;

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prevPage) => prevPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prevPage) => prevPage - 1);
        }
    };

    const PaginationControls: React.FC<{
        currentPage: number;
        totalPages: number;
        firstPicture: number;
        lastPicture: number;
        onPrevious: () => void;
        onNext: () => void;
    }> = ({ currentPage, totalPages, firstPicture, lastPicture, onPrevious, onNext }) => (
        <div id="pagination" style={{height:"5px", verticalAlign:"middle"}}>
            <div style={{float:"left"}}>Displaying photos {firstPicture+1} - {lastPicture}</div>
            <div style={{ textAlign: "right" }}>
                <button className="pageButton" onClick={onPrevious} disabled={currentPage === 1}>
                    &nbsp;&nbsp;{`<`}&nbsp;&nbsp;
                </button>
                <span style={{ margin: "0 15px" }}>Page {currentPage} of {totalPages}</span>
                <button className="pageButton" onClick={onNext} disabled={currentPage === totalPages}>
                    &nbsp;&nbsp;{`>`}&nbsp;&nbsp;
                </button>
            </div>
        </div>    
    );

    return (
        <div id="content">
            <h1>{eventDetails?.longTitle}</h1>
            <div style={{ float: "left", width: "620px" }}>
                <p>Click on the image to view purchasing information. There are several pages of photos for each category, click on the Previous and Next 
                    links to view each one. For a better viewing experience, maximize your browser window.</p>
                <h2>{selectedCategory?.title}</h2>
                <br style={{ clear: "both" }} />
                
                <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    firstPicture={indexOfFirstPicture}
                    lastPicture={indexOfLastPicture}
                    onPrevious={handlePreviousPage}
                    onNext={handleNextPage}
                />
                
                {/* Display pictures for the current page */}
                {currentPictures?.map((picture) => (
                    <div style={{ float: "left", marginLeft: '10px', marginBottom: '15px' }} key={picture.id}>
                        <div className="thumbnail">
                            <a href={`/photo/${eventId}/${picture.id}`}>
                                <img alt={picture?.id} className={Number(picture.width) === 600 ? 'thumbH' : 'thumbV'}
                                    src={eventDetails?.id && picture?.filename
                                        ? PageHelper.getImageSourceUrl(eventDetails.id, "thumb", picture.filename)
                                        : ""} />
                            </a>
                            <br />
                            <center>
                                <strong>Race No:</strong>&nbsp;{picture.caption}<br />
                                <strong>Image No:</strong>&nbsp;{picture.id}
                            </center>
                        </div>
                    </div>
                ))}
                <br style={{ clear: "both" }} />

                <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    firstPicture={indexOfFirstPicture}
                    lastPicture={indexOfLastPicture}
                    onPrevious={handlePreviousPage}
                    onNext={handleNextPage}
                />

                <br style={{ clear: "both" }} />
            </div>
            <div style={{ float: "left", marginLeft: '10px', width: '190px' }}>
                <p style={{ textAlign: "center" }}>
                    <a href={eventDetails?.organizer.href} target="_blank" rel="noreferrer">
                        <img src={eventDetails?.organizer.logo} alt="Event Logo" style={{ border: '0px' }} />
                    </a>
                </p>
                <h2>Event Categories</h2>
                <ul id="navsub">
                    {eventDetails?.categories.map((category) => (
                        <li key={category.id}>
                            <a href={`/event/${eventId}/${category.id}`} title={category.tooltip}>{category.title}</a>
                        </li>
                    ))}
                </ul>
            </div>
            <br style={{ clear: "both" }} />
            &nbsp;
        </div>
    );
};

export default Event;